import type { StoredConversation } from '../lib/history'

const dayFormat = new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric' })
const timeFormat = new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit' })

function stamp(value: number): string {
  const date = new Date(value)
  const today = new Date()
  const sameDay = date.toDateString() === today.toDateString()
  return sameDay ? timeFormat.format(date) : dayFormat.format(date)
}

interface HistoryPanelProps {
  conversations: StoredConversation[]
  activeId: string
  onOpen: (id: string) => void
  onDelete: (id: string) => void
  onNewChat: () => void
  onClose: () => void
}

export function HistoryPanel({
  conversations,
  activeId,
  onOpen,
  onDelete,
  onNewChat,
  onClose,
}: HistoryPanelProps) {
  return (
    <div className="drawer">
      <button
        type="button"
        className="drawer__scrim"
        aria-label="Close chat history"
        onClick={onClose}
      />
      <aside className="drawer__panel" aria-label="Chat history">
        <div className="drawer__head">
          <h2 className="drawer__title">Chat history</h2>
          <button type="button" className="drawer__close" onClick={onClose} aria-label="Close">
            &times;
          </button>
        </div>

        <button
          type="button"
          className="btn btn--primary drawer__new"
          onClick={() => {
            onNewChat()
            onClose()
          }}
        >
          New chat
        </button>

        {conversations.length === 0 ? (
          <p className="drawer__empty">
            Past conversations appear here once you send your first message.
          </p>
        ) : (
          <ul className="history">
            {conversations.map((conversation) => (
              <li
                key={conversation.id}
                className={`history__item${
                  conversation.id === activeId ? ' history__item--active' : ''
                }`}
              >
                <button
                  type="button"
                  className="history__open"
                  onClick={() => {
                    onOpen(conversation.id)
                    onClose()
                  }}
                >
                  <span className="history__title">{conversation.title}</span>
                  <span className="history__meta">
                    {stamp(conversation.updatedAt)} &middot; {conversation.messages.length} messages
                  </span>
                </button>
                <button
                  type="button"
                  className="history__delete"
                  onClick={() => onDelete(conversation.id)}
                  aria-label={`Delete chat: ${conversation.title}`}
                >
                  &times;
                </button>
              </li>
            ))}
          </ul>
        )}

        <p className="drawer__note">History is stored on this device only.</p>
      </aside>
    </div>
  )
}
